import React, { useEffect, useState } from 'react';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Flex, Text } from 'rebass';
import useNotify from '../../hooks/useNotify';
import { ApolloError } from '@apollo/client/errors';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Checkbox, Input, Label } from '../Form';
import ReactSelect from '../ReactSelect';
import { UtilityIconButton } from '../Button/UtilityIconButton';
import { ContactProfileInput } from '../Profile/types';

const LOAD_JOB = gql`
  query RecycleWaterJob($where: JSON, $limit: Int) {
    jobs(where: $where, limit: $limit) {
      id
      formData
      address(required: true) {
        id
        street1
        street2
        city
        state
        zip
        zipExt
      }
      recycledWaterInspection(required: true) {
        jobId
        waterSite(required: true) {
          id
          waterServices {
            id
            meterNumber
            siteNumber
          }
        }
        contact {
          id
          type
          name
          firstName
          lastName
          emails {
            subType
            value
          }
          phones {
            subType
            value
            extension
          }
        }
        supervisor {
          id
          type
          name
          firstName
          lastName
          emails {
            subType
            value
          }
          phones {
            subType
            value
            extension
          }
        }
      }
    }
  }
`;

const UPDATE_JOB_FORM_DATA = gql`
  mutation UpdateJobFormData($jobId: ID!, $formData: JSON!) {
    updateJobFormData(jobId: $jobId, formData: $formData) {
      id
      formData
    }
  }
`;

type CheckboxOption = {
  label: string;
  field: string;
};
const checkboxOptions: CheckboxOption[] = [
  {
    field: 'waterHeaters',
    label: 'Water Heaters',
  },
  {
    field: 'signage',
    label: 'Signage',
  },
  {
    field: 'coolingTower',
    label: 'Cooling Tower',
  },
  {
    field: 'thresholdValve',
    label: 'Threshold Valve',
  },
];

type ControllerCheck = {
  label: string;
  stations: StationCheck[];
};

type StationCheck = {
  label: string;
  comment: string;
  runoff: boolean;
  overspray: boolean;
  signs: boolean;
  ponding: boolean;
  tagOrCaps: boolean;
  brokenHead: boolean;
  mowCurb: boolean;
};

type StationOption = {
  label: string;
  value: keyof StationCheck;
};
const stationFlagOptions: StationOption[] = [
  {
    value: 'runoff',
    label: 'Runoff',
  },
  {
    value: 'overspray',
    label: 'Overspray',
  },
  {
    value: 'signs',
    label: 'Signs',
  },
  {
    value: 'ponding',
    label: 'Ponding',
  },
  {
    value: 'tagOrCaps',
    label: 'Tag/Caps',
  },
  {
    value: 'brokenHead',
    label: 'Broken Head',
  },
  {
    value: 'mowCurb',
    label: 'Mow Curb',
  },
];

const stationFlagDefaults = stationFlagOptions.reduce((check, option) => {
  check[option.value] = false as any;
  return check;
}, {} as Partial<StationCheck>);

type WaterSiteEditModalProps = {
  onClose: () => void;
  jobId: string;
};

type RecycledWaterFormData = Record<string, any>;

type JobWithInspectionAndData = {
  id: string;
  formData: RecycledWaterFormData;
  address: {
    id: string;
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zip: string;
    zipExt: string;
  };
  recycledWaterInspection: {
    jobId: string;
    waterSite: {
      waterServices: {
        id: string;
        meterNumber: string;
        siteNumber: string;
      }[];
    };
    contact?: ContactProfileInput;
    supervisor?: ContactProfileInput;
  };
};

const WaterSiteEditModal: React.FC<WaterSiteEditModalProps> = ({
  onClose,
  jobId,
}) => {
  const { showError, showSuccess } = useNotify();

  const onCompleted = (data: any) => {
    showSuccess('Results saved');
  };

  const onError = (error: ApolloError) => {
    showError('Error saving results', error);
  };

  const [
    updateFormData,
    { data: saveData, loading: saveLoading, error: saveError },
  ] = useMutation(UPDATE_JOB_FORM_DATA, {
    onCompleted,
    onError,
  });

  const [formDataInput, setFormDataInput] = useState<RecycledWaterFormData>({});

  const { error: itemError, data: itemData } = useQuery<{
    jobs: JobWithInspectionAndData[];
  }>(LOAD_JOB, {
    variables: {
      where: {
        id: jobId,
      },
      limit: null,
    },
    skip: Object.keys(formDataInput).length > 0,
  });

  useEffect(() => {
    if (itemData) {
      setFormDataInput(itemData.jobs[0].formData);
    }
  }, [itemData]);

  useEffect(() => {
    if (Object.keys(formDataInput).length === 0) {
      return;
    }
    updateFormData({
      variables: {
        jobId,
        formData: formDataInput,
      },
    });
  }, [formDataInput, updateFormData, jobId]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    updateField(name, value);
  };

  const handleCheckboxChange = (e: any) => {
    const { name, checked } = e.target;
    updateField(name, checked);
  };

  const updateField = <F extends keyof RecycledWaterFormData>(
    name: F,
    value?: RecycledWaterFormData[F]
  ) => {
    setFormDataInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const addController = () => {
    setFormDataInput((prevState) => {
      const controllers = Array.isArray(prevState.controllers)
        ? prevState.controllers.slice()
        : [];

      controllers.push({
        label: '',
        stations: [],
      });
      return {
        ...prevState,
        controllers,
      };
    });
  };

  const addStation = (controllerIndex: number) => {
    setFormDataInput((prevState) => {
      const controllers = (prevState.controllers ?? []).slice();
      const controller = controllers[controllerIndex];
      if (!controller) {
        return;
      }

      const stations = controller.stations ? controller.stations.slice() : [];

      stations.push({
        ...stationFlagDefaults,
        label: '',
        comment: '',
      });

      controllers[controllerIndex] = {
        ...controller,
        stations,
      };

      return {
        ...prevState,
        controllers,
      };
    });
  };

  const removeStation = (controllerIndex: number, stationIndex: number) => {
    setFormDataInput((prevState) => {
      const controllers = (prevState.controllers ?? []).slice();
      const controller = controllers[controllerIndex];
      if (!controller) {
        return;
      }

      const stations = controller.stations ? controller.stations.slice() : [];

      stations.splice(stationIndex, 1);

      controllers[controllerIndex] = {
        ...controller,
        stations,
      };

      return {
        ...prevState,
        controllers,
      };
    });
  };

  const onStationInput = (e: any) => {
    const { name, value, type, checked } = e.target;
    const stationIndex = e.target.getAttribute('data-station-index');
    const controllerIndex = e.target.getAttribute('data-controller-index');

    console.log(controllerIndex, stationIndex, { name, value, type, checked });

    setStationValue(
      controllerIndex,
      stationIndex,
      name,
      type === 'text' ? value : checked
    );
  };

  const setStationValue = <F extends keyof StationCheck>(
    controllerIndex: number,
    stationIndex: number,
    name: F,
    value: StationCheck[F]
  ) => {
    setStationValues(controllerIndex, stationIndex, {
      [name]: value,
    });
  };

  const setStationValues = (
    controllerIndex: number,
    stationIndex: number,
    values: Partial<StationCheck>
  ) => {
    setFormDataInput((prevState) => {
      const controllers = (prevState.controllers ?? []).slice();
      const controller = controllers[controllerIndex];
      if (!controller) {
        return;
      }

      const stations = controller.stations ? controller.stations.slice() : [];
      const station = stations[stationIndex];
      if (!station) {
        return;
      }

      stations[stationIndex] = {
        ...station,
        ...values,
      };

      controllers[controllerIndex] = {
        ...controller,
        stations,
      };

      return {
        ...prevState,
        controllers,
      };
    });
  };

  const saveInspection = onCompleted;
  const save = saveInspection;

  return (
    <Modal onClose={onClose} title={`Inspection Results`}>
      <ModalBody>
        <Flex flexDirection="column" mx={3} mb={3}>
          <Flex>
            <Flex flex={1} flexDirection="column">
              <Text variant="heading2" mt={3}>
                Potable
              </Text>
              <Flex>
                <Flex mr={2} flexDirection="column">
                  <Label htmlFor="potableMeters">Meters</Label>
                  <Input
                    id="potableMeters"
                    name="potableMeters"
                    type="number"
                    step={1}
                    min={0}
                    onChange={handleChange}
                    sx={{ flexGrow: 0 }}
                    minWidth={60}
                    maxWidth={90}
                    value={formDataInput.potableMeters}
                  />
                </Flex>
                <Flex mr={2} flexDirection="column">
                  <Label htmlFor="potablePressure">System PSI</Label>
                  <Input
                    id="potablePressure"
                    name="potablePressure"
                    type="number"
                    step={1}
                    min={0}
                    onChange={handleChange}
                    minWidth={60}
                    maxWidth={90}
                    value={formDataInput.potablePressure}
                  />
                </Flex>
              </Flex>
              <Label mt={1}>
                <Checkbox
                  name="potablePressureRegulators"
                  checked={formDataInput.potablePressureRegulators}
                  onClick={handleCheckboxChange}
                />
                Pressure Regulators?
              </Label>
            </Flex>
            <Flex flex={1} flexDirection="column">
              <Text variant="heading2" mt={3}>
                Recycled
              </Text>
              <Flex>
                <Flex mr={2} flexDirection="column">
                  <Label htmlFor="recycledMeters">Meters</Label>
                  <Input
                    id="recycledMeters"
                    name="recycledMeters"
                    type="number"
                    step={1}
                    min={0}
                    onChange={handleChange}
                    minWidth={60}
                    maxWidth={90}
                    value={formDataInput.recycledMeters}
                  />
                </Flex>
                <Flex mr={2} flexDirection="column">
                  <Label htmlFor="recycledPressure">System PSI</Label>
                  <Input
                    id="recycledPressure"
                    name="recycledPressure"
                    type="number"
                    step={1}
                    min={0}
                    onChange={handleChange}
                    minWidth={60}
                    maxWidth={90}
                    value={formDataInput.recycledPressure}
                  />
                </Flex>
              </Flex>
              <Label mt={1}>
                <Checkbox
                  name="recycledPressureRegulators"
                  checked={formDataInput.recycledPressureRegulators}
                  onClick={handleCheckboxChange}
                />
                Pressure Regulators?
              </Label>
            </Flex>
          </Flex>
          <Text variant="heading2" mt={4}>
            General
          </Text>
          <Flex sx={{ flexWrap: 'wrap' }}>
            {checkboxOptions.map((checkboxOption) => (
              <Label
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                key={checkboxOption.field}
                width="auto"
                mr={5}
              >
                <Checkbox
                  id={checkboxOption.field}
                  name={checkboxOption.field}
                  checked={formDataInput[checkboxOption.field]}
                  onClick={handleCheckboxChange}
                />
                {checkboxOption.label}
              </Label>
            ))}
          </Flex>
          {/*<Flex mt={3}>*/}
          {/*  <Flex mr={2} flexDirection="column">*/}
          {/*    <Label htmlFor="controllers">Controllers</Label>*/}
          {/*    <Input*/}
          {/*      id="controllers"*/}
          {/*      name="controllers"*/}
          {/*      type="number"*/}
          {/*      step={1}*/}
          {/*      min={0}*/}
          {/*      onChange={handleChange}*/}
          {/*      sx={{ flexGrow: 0 }}*/}
          {/*      minWidth={60}*/}
          {/*      maxWidth={90}*/}
          {/*      value={formDataInput.controllers}*/}
          {/*    />*/}
          {/*  </Flex>*/}
          {/*  <Flex mr={2} flexDirection="column">*/}
          {/*    <Label htmlFor="stations">Stations</Label>*/}
          {/*    <Input*/}
          {/*      id="stations"*/}
          {/*      name="stations"*/}
          {/*      type="number"*/}
          {/*      step={1}*/}
          {/*      min={0}*/}
          {/*      onChange={handleChange}*/}
          {/*      minWidth={60}*/}
          {/*      maxWidth={90}*/}
          {/*      value={formDataInput.stations}*/}
          {/*    />*/}
          {/*  </Flex>*/}
          {/*</Flex>*/}
          <Flex
            mt={3}
            justifyContent="space-between"
            sx={{ alignItems: 'center' }}
          >
            <Text variant="heading2" mt={4}>
              Station Checks
            </Text>
            <Button variant="primary" icon="plus" onClick={addController}>
              Add Controller
            </Button>
          </Flex>
          {Array.isArray(formDataInput.controllers) &&
            formDataInput.controllers?.map(
              (controller: ControllerCheck, controllerIndex: number) => (
                <Flex key={controllerIndex} flexDirection="column">
                  <Flex mt={4} flexDirection="column">
                    {/*<Label htmlFor="controllerName" variant="heading3">*/}
                    {/*  Controller*/}
                    {/*</Label>*/}
                    <Input
                      id="controllerName"
                      name="label"
                      onChange={handleChange}
                      minWidth={200}
                      maxWidth={500}
                      placeholder="controller"
                      // value={controller.label}
                    />
                  </Flex>
                  {controller.stations?.map(
                    (stationCheck: StationCheck, stationIndex: number) => (
                      <Flex
                        key={stationIndex}
                        my={2}
                        sx={{ alignItems: 'center' }}
                      >
                        <Flex
                          flexGrow={0}
                          flexShrink={0}
                          mr={2}
                          flexDirection="column"
                        >
                          <Input
                            autoFocus
                            data-station-index={stationIndex}
                            data-controller-index={controllerIndex}
                            id={`label${stationIndex}`}
                            name="label"
                            placeholder="station"
                            minWidth={200}
                            onChange={onStationInput}
                            value={stationCheck.label}
                          />
                        </Flex>
                        <Flex flexGrow={1} flexWrap="wrap" mr={2}>
                          <ReactSelect
                            isMulti
                            placeholder="issues"
                            menuPlacement="top"
                            closeMenuOnSelect={false}
                            blurInputOnSelect={false}
                            isClearable={false}
                            options={stationFlagOptions}
                            value={stationFlagOptions.filter((option) => {
                              return stationCheck[option.value];
                            })}
                            onChange={(values) => {
                              const selectedFlags = values.reduce(
                                (check, option) => {
                                  check[option.value] = true as any;
                                  return check;
                                },
                                {} as Partial<StationCheck>
                              );

                              setStationValues(controllerIndex, stationIndex, {
                                ...stationFlagDefaults,
                                ...selectedFlags,
                              });
                            }}
                          />
                          {/*{stationFlagOptions.map((stationOption) => (*/}
                          {/*  <Label*/}
                          {/*    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}*/}
                          {/*    key={stationOption.value}*/}
                          {/*    width="auto"*/}
                          {/*    mr={3}*/}
                          {/*  >*/}
                          {/*    <Checkbox*/}
                          {/*      data-stationIndex={stationIndex}*/}
                          {/*      id={`${stationOption.value}${stationIndex}`}*/}
                          {/*      name={stationOption.value}*/}
                          {/*      checked={stationCheck[stationOption.value] as boolean}*/}
                          {/*      onClick={onStationInput}*/}
                          {/*    />*/}
                          {/*    {stationOption.label}*/}
                          {/*  </Label>*/}
                          {/*))}*/}
                        </Flex>
                        <Flex
                          flexGrow={0}
                          flexShrink={0}
                          mr={2}
                          flexDirection="column"
                        >
                          <Input
                            data-station-index={stationIndex}
                            data-controller-index={controllerIndex}
                            id={`comment${stationIndex}`}
                            name="comment"
                            minWidth={200}
                            placeholder="comments"
                            onChange={onStationInput}
                            value={stationCheck.comment}
                          />
                        </Flex>
                        <UtilityIconButton
                          dark
                          icon="x"
                          onClick={(e: any) =>
                            removeStation(controllerIndex, stationIndex)
                          }
                        />
                      </Flex>
                    )
                  )}
                  <Flex mt={1}>
                    <Button
                      variant="primary"
                      icon="plus"
                      onClick={() => addStation(controllerIndex)}
                    >
                      Add Station
                    </Button>
                  </Flex>
                </Flex>
              )
            )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>Done</Button>
        {/*<Button icon="save" onClick={save}>*/}
        {/*  Save*/}
        {/*</Button>*/}
        {/*<Button variant="outline" icon="slash-circle" onClick={onClose}>*/}
        {/*  Cancel*/}
        {/*</Button>*/}
      </ModalFooter>
    </Modal>
  );
};

export default WaterSiteEditModal;
